import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useId, useRef } from 'react';
import { cn } from '../utils/cn';
/* Lässt ModalTitle seine generierte id melden, damit der Dialog über
   aria-labelledby einen barrierefreien Namen bekommt. */
const ModalTitleIdContext = createContext(undefined);
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
function focusables(root) {
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true');
}
/**
 * Overlay-Dialog auf Basis eines div-Overlays (`.sec-modal*`). Verwaltet
 * Offen-Zustand, Escape-Schließen, Scroll-Lock, aria und den Fokus:
 * beim Öffnen wandert der Fokus in den Dialog (erstes fokussierbares Element,
 * sonst das Panel), Tab/Shift-Tab bleiben im Dialog, beim Schließen kehrt der
 * Fokus zum auslösenden Element zurück. Für ein natives `<dialog>` siehe
 * stattdessen `Dialog`.
 */
export function Modal({ open, onOpenChange, children, ariaLabel }) {
    const titleId = useId();
    const overlayRef = useRef(null);
    const restoreRef = useRef(null);
    const handleKey = useCallback((e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            onOpenChange(false);
            return;
        }
        if (e.key !== 'Tab' || !overlayRef.current)
            return;
        const items = focusables(overlayRef.current);
        if (items.length === 0) {
            e.preventDefault();
            overlayRef.current.focus();
            return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !overlayRef.current.contains(active))) {
            e.preventDefault();
            last.focus();
        }
        else if (!e.shiftKey && (active === last || !overlayRef.current.contains(active))) {
            e.preventDefault();
            first.focus();
        }
    }, [onOpenChange]);
    useEffect(() => {
        if (!open)
            return;
        const previousOverflow = document.body.style.overflow;
        restoreRef.current = document.activeElement ?? null;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKey);
        // Initialfokus: erstes fokussierbares Element im Panel, sonst der Dialog selbst.
        const node = overlayRef.current;
        if (node) {
            const panel = node.querySelector('.sec-modal') ?? node;
            const target = focusables(panel).find((el) => !el.closest('.sec-modal__footer')) ?? focusables(panel)[0] ?? node;
            target.focus({ preventScroll: true });
        }
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKey);
            const back = restoreRef.current;
            restoreRef.current = null;
            if (back && typeof back.focus === 'function' && document.contains(back))
                back.focus({ preventScroll: true });
        };
    }, [open, handleKey]);
    if (!open)
        return null;
    return (_jsx("div", { ref: overlayRef, className: "sec-modal-overlay", role: "dialog", "aria-modal": "true", "aria-labelledby": ariaLabel ? undefined : titleId, "aria-label": ariaLabel, tabIndex: -1, onClick: () => onOpenChange(false), children: _jsx(ModalTitleIdContext.Provider, { value: titleId, children: children }) }));
}
/** Panel; stoppt Klick-Propagation, damit Klicks im Dialog nicht schließen. */
export function ModalContent({ children, className }) {
    return (_jsx("div", { className: cn('sec-modal', className), onClick: (e) => e.stopPropagation(), children: children }));
}
export function ModalHeader({ children, className }) {
    return _jsx("div", { className: cn('sec-modal__header', className), children: children });
}
/** Titel des Dialogs (h2 wie `DialogTitle`); liefert den barrierefreien Namen. */
export function ModalTitle({ children, className }) {
    const id = useContext(ModalTitleIdContext);
    return (_jsx("h2", { id: id, className: cn('sec-modal__title', className), children: children }));
}
export function ModalDescription({ children, className }) {
    return _jsx("p", { className: cn('sec-modal__desc', className), children: children });
}
export function ModalBody({ children, className }) {
    return _jsx("div", { className: cn('sec-modal__body', className), children: children });
}
export function ModalFooter({ children, className }) {
    return _jsx("div", { className: cn('sec-modal__footer', className), children: children });
}
